import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { queryAllTransactions as queryAllTransactionsApi } from '../../util/api';
import {
    recievePayPalAccessToken,
    showOrderDetails,
    refundCapturedPayment,
    createReferencedPayoutItem,
    getReferencedPayoutItem,
    getSharetribeTransactionDetails,
    updateSharetribeTransactionMetadata,
    transitionTxOperator,
} from '../../ducks/paypalDuck';

const sortedTransactions = txs =>
    reverse(
        sortBy(txs, tx => {
            return tx.attributes ? tx.attributes.lastTransitionedAt : null;
        })
    );

// ================ Action types ================ //

export const FETCH_TRANSACTIONS_REQUEST = 'app/BulkTransactionManagementPage/FETCH_TRANSACTIONS_REQUEST';
export const FETCH_TRANSACTIONS_SUCCESS = 'app/BulkTransactionManagementPage/FETCH_TRANSACTIONS_SUCCESS';
export const FETCH_TRANSACTIONS_ERROR = 'app/BulkTransactionManagementPage/FETCH_TRANSACTIONS_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
    entities.map(entity => ({
        id: entity.id,
        type: entity.type,
    }));

const initialState = {
    fetchInProgress: false,
    fetchError: null,
    pagination: null,
    transactionRefs: [],
};

export default function bulkTransactionManagementPageReducer(state = initialState, action = {}) {
    const { type, payload } = action;
    switch (type) {
        case FETCH_TRANSACTIONS_REQUEST:
            return { ...state, fetchInProgress: true, fetchError: null };
        case FETCH_TRANSACTIONS_SUCCESS: {
            const transactions = sortedTransactions(payload.data.data);
            return {
                ...state,
                fetchInProgress: false,
                transactionRefs: entityRefs(transactions),
                pagination: payload.data.meta,
            };
        }
        case FETCH_TRANSACTIONS_ERROR:
            console.error(payload); // eslint-disable-line
            return { ...state, fetchInProgress: false, fetchError: payload };

        default:
            return state;
    }
}

// ================ Action creators ================ //

const fetchTransactionsRequest = () => ({ type: FETCH_TRANSACTIONS_REQUEST });
const fetchTransactionsSuccess = response => ({
    type: FETCH_TRANSACTIONS_SUCCESS,
    payload: response,
});
const fetchTransactionsError = e => ({
    type: FETCH_TRANSACTIONS_ERROR,
    error: true,
    payload: e,
});

// ================ Thunks ================ //

const PAGE_SIZE = 20;

export const loadData = (params, search) => (dispatch, getState, sdk) => {
    dispatch(fetchTransactionsRequest());

    const { page = 1 } = parse(search);

    // Use server-side Integration API to query all transactions (not user-scoped)
    return queryAllTransactionsApi({ data: { page, perPage: PAGE_SIZE } })
        .then(response => {
            dispatch(addMarketplaceEntities({ data: response }));
            dispatch(fetchTransactionsSuccess({ data: response }));
            return response;
        })
        .catch(e => {
            dispatch(fetchTransactionsError(storableError(e)));
            throw e;
        });
};

// ================ PayPal Helper Functions ================ //
// Re-exported from paypalDuck for use in the page component

const serializeError = (e) => {
    return { status: 'failed', error_message: e.message, data: e.data };
};

export const getPayPalAccessToken = async () => {
    try {
        return await recievePayPalAccessToken();
    } catch (error) {
        return serializeError(error);
    }
};

export const showPayPalOrderDetails = async (orderId, token, merchantId) => {
    try {
        return await showOrderDetails(orderId, token, merchantId);
    } catch (error) {
        return serializeError(error);
    }
};

export const refundPayPalCapturedPayment = async (captureId, token, merchantId, refundAmount, paymentInstruction) => {
    try {
        return await refundCapturedPayment(captureId, token, merchantId, refundAmount, paymentInstruction);
    } catch (error) {
        return serializeError(error);
    }
};

export const createReferencedPayPalPayoutItem = async (token, merchantId, captureId) => {
    try {
        return await createReferencedPayoutItem(token, merchantId, captureId);
    } catch (error) {
        return serializeError(error);
    }
};

export const getReferencedPayPalPayoutItem = async (token, merchantId, itemId) => {
    try {
        return await getReferencedPayoutItem(token, merchantId, itemId);
    } catch (error) {
        return serializeError(error);
    }
};

export const getTransactionDetails = async (transactionId) => {
    try {
        return await getSharetribeTransactionDetails(transactionId);
    } catch (error) {
        return serializeError(error);
    }
};

export const updateTransactionMetadata = async (transactionId, metadata) => {
    try {
        return await updateSharetribeTransactionMetadata(transactionId, metadata);
    } catch (error) {
        return serializeError(error);
    }
};

export const transitionTxPrivileged = async (transactionId, transition, params) => {
    try {
        return await transitionTxOperator(transactionId, transition, params);
    } catch (error) {
        return serializeError(error);
    }
};
